import ApiService from "../../../services/ApiService";
import Logger from "../../../services/Logger";
import { getAdminContentLanguage } from "../../../services/LangPrefs";

export const DRAFT = "DRAFT";
export const READY_TO_REVIEW = "READY_TO_REVIEW";
export const REVIEWED = "REVIEWED";
export const PUBLISHED = "PUBLISHED";
export const DISCARDED = "DISCARDED";
export const DISABLED = "DISABLED";

const AdminGridBoardService = {
    requestList: async (paginationModel, sort, setRows, setRowCount, keyword, state, selectedGenreId, language, type, tags, free, difficultyLevel, gridSize) => {
        let param = {
            page: paginationModel.page,
            pageSize: paginationModel.pageSize,
            sort: sort.map((s) => `${s.field}:${s.sort}`).join(","),
            keyword: keyword ? encodeURIComponent(keyword) : "",
        };

        if (state) {
            param.state = state;
        }

        if (selectedGenreId && selectedGenreId !== "all") {
            param.genreId = selectedGenreId;
        }

        // Include selected language (en/fr) so API filters by it
        if (language) {
            param.language = language;
        }

        // Add type filter if provided
        if (type && type !== "all") {
            param.type = type;
        }

        // Add tags filter if provided (comma-separated string)
        if (tags && Array.isArray(tags) && tags.length > 0) {
            // Convert tags to strings and normalize to lowercase for case-insensitive matching
            const tagStrings = tags.map(tag => {
                let tagString;
                if (typeof tag === 'string') {
                    tagString = tag;
                } else {
                    tagString = tag.tag || String(tag);
                }
                // Normalize to lowercase for case-insensitive filtering
                return tagString.toLowerCase().trim();
            }).filter(tag => tag.length > 0); // Remove empty tags
            param.tags = tagStrings.join(",");
        }

        // Add free filter if provided (true/false/null)
        if (free !== null && free !== undefined) {
            param.free = free === true || free === "true";
        }

        // Add difficulty level filter if provided (EASY/MEDIUM/HARD)
        if (difficultyLevel && difficultyLevel !== "all") {
            param.difficultyLevel = difficultyLevel;
        }

        if (gridSize === "classic" || gridSize === "mini") {
            param.sizeFamily = gridSize;
        } else if (gridSize === 9 || gridSize === "9") {
            param.sizeFamily = "mini";
        } else if (gridSize === 12 || gridSize === "12") {
            param.sizeFamily = "classic";
        }

        const result = await ApiService.getData("/v1/puzzle/grid-tpl", param);
        setRows(result.content || []);
        const totalCount = result.page?.totalElements ?? 0;
        if (totalCount === 0 && result.content && result.content.length > 0) {
            Logger.warn("API response missing totalElements. Response structure:", result);
        }
        setRowCount(totalCount);

        return result;
    },

    requestHwTpl: async (id) => {
        try {
            const result = await ApiService.getData(`/v1/puzzle/grid-tpl/${id}`);
            return result;
        } catch (error) {
            Logger.error(`Error fetching puzzle with ID ${id}:`, error);
            throw error;
        }
    },

    updateHwTplState: async (id, newState, puzzleType = null, details = null, title = null, hiddenMessage = null) => {
        try {
            const payload = { state: newState };
            if (puzzleType) {
                payload.type = puzzleType;
            }
            if (details) {
                payload.details = details;
            }
            if (title) {
                payload.title = title;
            }
            if (hiddenMessage) {
                payload.hiddenMessage = hiddenMessage;
            }
            const result = await ApiService.putData(`/v1/puzzle/grid-tpl/${id}/state`, payload);
            
            // Check if the response indicates failure
            if (result && result.code === "FAILURE") {
                const errorMessage = result.message || "Failed to update puzzle state";
                throw new Error(errorMessage);
            }
            
            return result;
        } catch (error) {
            Logger.error(`Error updating puzzle state for ID ${id}:`, error);
            throw error;
        }
    },

    /**
     * @param {boolean} makeNow - Set publishedAt to current date and time.
     */
    publishHwTpl: async (
        id,
        publishComment,
        details,
        isFree,
        makeNow = true
    ) => {
        try {
            const payload = {
                comment: publishComment,
                details: details,
                isFree: isFree,
                makeNow: Boolean(makeNow),
            };
            const result = await ApiService.putData(`/v1/puzzle/grid-tpl/${id}/publish`, payload);
            return result;
        } catch (error) {
            Logger.error(`Error updating puzzle state for ID ${id}:`, error);
            throw error;
        }
    },

    setClue: async (word, definition, puzzleId, saveAsHiddenMessage = false) => {
        try {
            const payload = {
                definition: definition
            };

            if (saveAsHiddenMessage) {
                payload.hiddenMessage = word;
            }

            const result = await ApiService.putData(`/v1/puzzle/grid-tpl/${puzzleId}/set-clue`, payload);
            return result;
        } catch (error) {
            Logger.error(`Error setting clue for word "${word}" in puzzle ${puzzleId}:`, error);
            throw error;
        }
    },

    fillMore: async (tplId) => {
        try {
            const result = await ApiService.postData(`/v1/puzzle/grid-tpl/${tplId}/fill-more`, {});
            return result;
        } catch (error) {
            Logger.error(`Error filling empty cells for puzzle ${tplId}:`, error);
            throw error;
        }
    },

    /** Fill empty cells with random letters (WORD_SEARCH). */
    fillRandomLetters: async (tplId) => {
        try {
            const result = await ApiService.postData(`/v1/puzzle/grid-pack/tpl/${tplId}/fill`, {});
            return result;
        } catch (error) {
            Logger.error(`Error filling random letters for puzzle ${tplId}:`, error);
            throw error;
        }
    },

    updatePuzzleCoordinates: async (puzzleId, filledCoordinates, newWord) => {
        try {
            let payload = {
                hwTplId: puzzleId,
                updatedCoords: JSON.stringify(filledCoordinates),
            };

            if (newWord) {
                payload.newWord = newWord;
            }

            const result = await ApiService.putData(`/v1/puzzle/grid-tpl/filled_coords`, payload);

            return result;
        } catch (error) {
            Logger.error(`Error updating puzzle coordinates for puzzle ${puzzleId}:`, error);
            throw error;
        }
    },

    markAsDiscarded: async (tplId) => {
        try {
            const result = await ApiService.putData(`/v1/puzzle/grid-tpl/${tplId}/state`, { state: DISCARDED });
            return result;
        } catch (error) {
            Logger.error(`Error marking template as discarded with ID ${tplId}:`, error);
            throw error;
        }
    },

    setFree: async (gridTplId, isFree) => {
        try {
            const result = await ApiService.putData(`/v1/puzzle/grid-tpl/${gridTplId}/set-free/${!!isFree}`);
            return result;
        } catch (error) {
            Logger.error(`Error setting free for grid template ${gridTplId}:`, error);
            throw error;
        }
    },

    cleanUp: async (hwTpl) => {
        try {
            // Logger.log("hwTpl", hwTpl);
            // Logger.log("coordinates", JSON.parse(hwTpl.coordinates));

            const filledCoordinates = JSON.parse(hwTpl.filledCoordinates);
            const updatedCoordinates = filledCoordinates
                .filter((coord) => coord.text !== null)
                .map(coord => ({ ...coord, text: coord.text.toLowerCase() }));
            // Logger.log("updatedCoordinates", updatedCoordinates);

            const result = await ApiService.putData(`/v1/puzzle/grid-tpl/${hwTpl.id}/cleanUp`, {
                updatedCoordinates: JSON.stringify(updatedCoordinates)
            });
            return result;
        } catch (error) {
            Logger.error(`Error cleaning up puzzle with ID ${hwTpl.id}:`, error);
            throw error;
        }
    },

    updatePuzzleInfo: async (hwTplId, title, isFree, tags = null) => {
        try {
            const payload = {
                title: title,
                isFree: isFree
            };
            if (tags !== null && tags !== undefined && Array.isArray(tags)) {
                payload.tags = tags.map((tag) => String(tag).trim()).filter((tag) => tag.length > 0);
            }
            const result = await ApiService.putData(`/v1/puzzle/grid-tpl/${hwTplId}/info`, payload);
            return result;
        } catch (error) {
            Logger.error(`Error updating puzzle info with ID ${hwTplId}:`, error);
            throw error;
        }
    },


    updateDifficultyLevel: async (tplId, difficultyLevel) => {
        try {
            const result = await ApiService.putData(`/v1/puzzle/grid-tpl/${tplId}/difficulty`, {
                difficultyLevel: difficultyLevel
            });
            return result;
        } catch (error) {
            Logger.error(`Error updating difficulty level for puzzle ${tplId}:`, error);
            throw error;
        }
    },


    /**
     * Generate word network for a puzzle
     * @param {string} tplId - The puzzle template ID
     * @returns {Promise<Object>} Object containing response code
     */
    generateWordNetwork: async (tplId) => {
        try {
            const result = await ApiService.putData(`/v1/puzzle/grid-tpl/${tplId}/gen-word-network`);
            return result;
        } catch (error) {
            Logger.error(`Error generating word network for puzzle ${tplId}:`, error);
            throw error;
        }
    },

    /**
     * Refresh word network for a puzzle
     * @param {string} tplId - The puzzle template ID
     * @param {string} language - The puzzle language (e.g., "english", "french")
     * @returns {Promise<Object>} Object containing response code
     */
    refreshWordNetwork: async (tplId, language) => {
        try {
            const result = await ApiService.putData(`/v1/puzzle/grid-tpl/${tplId}/refresh-word-network?language=${language}`);
            return result;
        } catch (error) {
            Logger.error(`Error refreshing word network for puzzle ${tplId}:`, error);
            throw error;
        }
    },

    /**
     * Refresh difficulty level for a puzzle
     * @param {string} tplId - The puzzle template ID
     * @returns {Promise<Object>} Object containing response code and updated difficulty level
     */
    refreshDifficultyLevel: async (tplId) => {
        try {
            const result = await ApiService.putData(`/v1/puzzle/grid-tpl/${tplId}/refresh-difficulty-level`);
            return result;
        } catch (error) {
            Logger.error(`Error refreshing difficulty level for puzzle ${tplId}:`, error);
            throw error;
        }
    },

    /**
     * Apply title hook for a puzzle
     * @param {string} tplId - The puzzle template ID
     * @returns {Promise<Object>} Object containing response code
     */
    applyTitleHook: async (tplId) => {
        try {
            const result = await ApiService.putData(`/v1/puzzle/grid-tpl/${tplId}/title-hook`);
            return result;
        } catch (error) {
            Logger.error(`Error applying title hook for puzzle ${tplId}:`, error);
            throw error;
        }
    },

    /**
     * Get fill-assoc candidates (GET). Return shape matches final-4 / match-keyword API.
     * @param {string} tplId - The puzzle template ID
     * @returns {Promise<{ wordsFound: number, wordsWithPositions: Array }>}
     */
    getFillAssocBedrock: async (tplId) => {
        const result = await ApiService.getData(`/v1/puzzle/grid-tpl/${tplId}/fill-assoc/bedrock`);
        return {
            wordsFound: result?.wordsFound ?? 0,
            wordsWithPositions: Array.isArray(result?.wordsWithPositions) ? result.wordsWithPositions : []
        };
    },

    /**
     * Fill rest of empty cells using Bedrock AI (GET). Return shape matches match-keyword API.
     * @param {string} tplId - The puzzle template ID
     * @returns {Promise<{ wordsFound: number, wordsWithPositions: Array }>}
     */
    getFillRestBedrock: async (tplId) => {
        const result = await ApiService.getData(`/v1/puzzle/grid-tpl/${tplId}/fill-rest/bedrock`);
        return {
            wordsFound: result?.wordsFound ?? 0,
            wordsWithPositions: Array.isArray(result?.wordsWithPositions) ? result.wordsWithPositions : []
        };
    },

    /**
     * Get Final 4 candidates for grid template. Return shape matches match-keyword API.
     * @param {string} tplId - The puzzle template ID
     * @returns {Promise<{ wordsFound: number, wordsWithPositions: Array }>}
     */
    getFinal4Candidates: async (tplId) => {
        const result = await ApiService.getData(`/v1/puzzle/grid-tpl/${tplId}/final-4`);
        return {
            wordsFound: result?.wordsFound ?? 0,
            wordsWithPositions: Array.isArray(result?.wordsWithPositions) ? result.wordsWithPositions : []
        };
    },

    /**
     * Check if a hidden message is duplicated
     * @param {string} hiddenMessage - The hidden message to check
     * @param {string} language - The language of the puzzle
     * @param {string} excludeTplId - Optional template ID to exclude from the check
     * @returns {Promise<Object>} Object containing isDuplicate, count, and templates
     */
    checkHiddenMessageDuplicate: async (hiddenMessage, language, excludeTplId = null) => {
        try {
            const params = {
                hiddenMessage: hiddenMessage,
                language: language
            };
            if (excludeTplId) {
                params.excludeTplId = excludeTplId;
            }
            const result = await ApiService.getData("/v1/puzzle/grid-tpl/check-hidden-message", params);
            return result;
        } catch (error) {
            Logger.error(`Error checking hidden message duplicate:`, error);
            throw error;
        }
    },

    /**
     * Rearrange words in a word search puzzle
     * @param {string} tplId - The puzzle template ID
     * @returns {Promise<Object>} Object containing response code
     */
    rearrangeWords: async (tplId) => {
        try {
            const result = await ApiService.putData(`/v1/puzzle/grid-tpl/${tplId}/rearrange`, {});
            return result;
        } catch (error) {
            Logger.error(`Error rearranging words for puzzle ${tplId}:`, error);
            throw error;
        }
    },

    /**
     * Fill one word from dictionary into an empty slot
     * @param {string} tplId - The puzzle template ID
     * @returns {Promise<Object>} Object containing filledCount, insertedWords, message
     */
    fillPattern: async (tplId) => {
        try {
            const result = await ApiService.putData(`/v1/puzzle/grid-tpl/${tplId}/fill-pattern`);
            return result;
        } catch (error) {
            Logger.error(`Error filling word from dictionary for puzzle ${tplId}:`, error);
            throw error;
        }
    },

    /**
     * Get candidate words for filling empty pattern slots
     * @param {string} tplId - The puzzle template ID
     * @returns {Promise<Array>} Array of candidate items with word, meaning, direction, positions, info
     */
    getCandidatesPatternTags: async (tplId) => {
        try {
            const result = await ApiService.getData(`/v1/puzzle/grid-tpl/${tplId}/fill-candidates`);
            return Array.isArray(result) ? result : [];
        } catch (error) {
            Logger.error(`Error fetching candidates pattern tags for puzzle ${tplId}:`, error);
            throw error;
        }
    },

    /**
     * Match keyword in grid template - find words matching the keyword pattern
     * @param {string} tplId - The puzzle template ID
     * @param {string|null} keyword - Optional search keyword (trimmed; omit for all)
     * @returns {Promise<{ wordsFound: number, wordsWithPositions: Array<{ word: string, positions: Array<{ row: number, col: number }> }> }>}
     */
    getMatchKeyword: async (tplId, keyword) => {
        const params = {};
        if (keyword != null && String(keyword).trim() !== "") {
            params.keyword = String(keyword).trim();
        }
        const result = await ApiService.getData(`/v1/puzzle/grid-tpl/${tplId}/match-keyword`, params);
        return {
            wordsFound: result?.wordsFound ?? 0,
            wordsWithPositions: Array.isArray(result?.wordsWithPositions) ? result.wordsWithPositions : []
        };
    },

    /**
     * Batch-publish newest REVIEWED grid templates for a tag.
     * POST /v1/puzzle/grid-tpl/publish?batchSize=…&language=…&tag=…
     */
    publishReviewed: async (batchSize = 1, language, tag, puzzleType) => {
        try {
            const n = Number(batchSize);
            const clamped = Number.isFinite(n) ? Math.min(1000, Math.max(1, Math.round(n))) : 1;
            const params = new URLSearchParams();
            params.set("batchSize", String(clamped));
            const lang = language != null ? String(language).trim() : "";
            if (lang) params.set("language", lang);
            const tagTrim = tag != null ? String(tag).trim() : "";
            if (tagTrim) params.set("tag", tagTrim);
            const type = puzzleType != null ? String(puzzleType).trim().toUpperCase() : "";
            if (type === "HIDDEN_MESSAGE" || type === "WORD_SEARCH") {
                params.set("puzzleType", type);
            }
            const response = await ApiService.postData(
                `/v1/puzzle/grid-tpl/publish?${params.toString()}`,
                {}
            );
            if (response?.code === "FAILURE") {
                return { success: false, error: response.message || "Publish failed", data: null };
            }
            return {
                success: true,
                data: {
                    requestedBatchSize:
                        response?.requestedBatchSize ?? response?.data?.requestedBatchSize ?? clamped,
                    publishedCount: response?.publishedCount ?? response?.data?.publishedCount ?? 0,
                    tplIds: response?.tplIds ?? response?.data?.tplIds ?? [],
                    raw: response,
                },
            };
        } catch (e) {
            Logger.error("AdminGridBoardService.publishReviewed:", e);
            return { success: false, error: e.message || "Failed to publish grid puzzles", data: null };
        }
    }
};

export default AdminGridBoardService;

